import type { NoteId } from '../types';
import { getBoomChannelGain } from '../utils/mixVolumes';
import {
  getAudioContext,
  loadTrimmedSample,
  playSampleNow,
  resolveSampleUrl,
} from './webAudioEngine';

const BOOMWHACKER_CANDIDATES: Record<Exclude<NoteId, 'rest'>, string[]> = {
  do: [
    '/junior-assets/sounds/Boomwhacker C.wav',
    '/junior-assets/sounds/Boomwhacker C.mp3',
    '/junior-assets/sounds/do.wav',
  ],
  re: [
    '/junior-assets/sounds/Boomwhacker D.wav',
    '/junior-assets/sounds/Boomwhacker D.mp3',
    '/junior-assets/sounds/re.wav',
  ],
  mi: [
    '/junior-assets/sounds/Boomwhacker E.wav',
    '/junior-assets/sounds/Boomwhacker E.mp3',
    '/junior-assets/sounds/mi.wav',
  ],
  fa: [
    '/junior-assets/sounds/Boomwhacker F.wav',
    '/junior-assets/sounds/Boomwhacker F.mp3',
    '/junior-assets/sounds/Fa.wav',
  ],
  sol: [
    '/junior-assets/sounds/Boomwhacker G.wav',
    '/junior-assets/sounds/Boomwhacker G.mp3',
    '/junior-assets/sounds/Sol.wav',
  ],
  la: [
    '/junior-assets/sounds/Boomwhacker A.wav',
    '/junior-assets/sounds/Boomwhacker A.mp3',
    '/junior-assets/sounds/La.wav',
  ],
  si: [
    '/junior-assets/sounds/Boomwhacker B.wav',
    '/junior-assets/sounds/Boomwhacker B.mp3',
    '/junior-assets/sounds/Si.wav',
  ],
};

const NOTE_TRIM_THRESHOLD = 0.01;

let preloadPromise: Promise<void> | null = null;

function boomKey(note: Exclude<NoteId, 'rest'>): string {
  return `boom:${note}`;
}

async function ensureNoteLoaded(note: Exclude<NoteId, 'rest'>): Promise<boolean> {
  const key = boomKey(note);
  const url = await resolveSampleUrl(BOOMWHACKER_CANDIDATES[note]);
  if (!url) return false;
  const sample = await loadTrimmedSample(key, url, NOTE_TRIM_THRESHOLD);
  return sample !== null;
}

export async function preloadBoomwhackerSamples(): Promise<void> {
  if (!preloadPromise) {
    preloadPromise = (async () => {
      await getAudioContext();
      await Promise.all(
        (Object.keys(BOOMWHACKER_CANDIDATES) as Exclude<NoteId, 'rest'>[]).map((note) =>
          ensureNoteLoaded(note),
        ),
      );
    })();
  }
  return preloadPromise;
}

export function playBoomwhackerSample(
  note: NoteId,
  durationMs: number,
  volume = getBoomChannelGain(),
): void {
  if (note === 'rest') return;

  void (async () => {
    const loaded = await ensureNoteLoaded(note);
    if (!loaded) return;
    const sec = Math.min(Math.max(durationMs, 80), 3000) / 1000;
    await playSampleNow(boomKey(note), volume, sec);
  })();
}

export function previewBoomwhackerSample(note: NoteId): void {
  if (note === 'rest') return;
  void (async () => {
    const loaded = await ensureNoteLoaded(note);
    if (!loaded) return;
    await playSampleNow(boomKey(note), getBoomChannelGain(), 0.45);
  })();
}
